import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import meetingService from '../../../services/meetingService';
import { supabase } from '../../../lib/supabase';

const MeetingScheduler = ({ teamId, teamMembers = [], allUsers = [], isPCFAdmin = false }) => {
  const { user } = useAuth();
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedMeeting, setExpandedMeeting] = useState(null);
  const [cityOfficial, setCityOfficial] = useState(null);
  const [mentor, setMentor] = useState(null);
  const [pcfAdmins, setPcfAdmins] = useState([]);
  const [usersWithoutTeam, setUsersWithoutTeam] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [newMeeting, setNewMeeting] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    duration: '60',
    type: 'team',
    attendees: [],
    location: 'online',
    locationDetails: '',
    googleMeetLink: ''
  });

  const meetingTypes = [
    { value: 'team', label: 'Team Meeting' },
    { value: 'city-official', label: 'City Official Check-in' },
    { value: 'mentor', label: 'Mentor Session' },
    { value: 'presentation', label: 'Presentation' }
  ];

  const durationOptions = [
    { value: '30', label: '30 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' }
  ];

  const locationOptions = [
    { value: 'online', label: 'Online' },
    { value: 'campus', label: 'Campus Location' },
    { value: 'city-hall', label: 'City Hall' },
    { value: 'other', label: 'Other Location' }
  ];

  // Enhanced: Fetch team members with full names and users without teams
  useEffect(() => {
    const fetchAttendeeOptions = async () => {
      // Skip fetching if we're in PCF Admin mode with allUsers already provided
      if (isPCFAdmin && allUsers?.length > 0) {
        return;
      }

      try {
        // Fetch team data for city official and mentor
        const { data: teamData } = await supabase?.from('teams')?.select('point_of_contact, mentor')?.eq('id', teamId)?.single();

        if (teamData) {
          // Fetch city official
          if (teamData?.point_of_contact) {
            const { data: officialData } = await supabase?.from('user_profiles')?.select('id, full_name, email')?.ilike('full_name', teamData?.point_of_contact)?.single();
            
            if (officialData) setCityOfficial(officialData);
          }

          // Fetch mentor
          if (teamData?.mentor) {
            const { data: mentorData } = await supabase?.from('user_profiles')?.select('id, full_name, email')?.ilike('full_name', teamData?.mentor)?.single();
            
            if (mentorData) setMentor(mentorData);
          }
        }

        // Fetch PCF admins
        const { data: adminsData } = await supabase?.from('role_email_authorizations')?.select('email')?.eq('role', 'pcf_admin')?.eq('is_active', true);

        if (adminsData && adminsData?.length > 0) {
          const adminEmails = adminsData?.map(a => a?.email);
          const { data: adminProfiles } = await supabase?.from('user_profiles')?.select('id, full_name, email')?.in('email', adminEmails);
          
          if (adminProfiles) setPcfAdmins(adminProfiles);
        }

        // Fetch users without team assignment
        const { data: usersWithoutTeamData } = await supabase
          ?.from('user_profiles')
          ?.select('id, full_name, email, role')
          ?.not('id', 'in', `(SELECT user_id FROM team_members)`)
          ?.neq('role', 'pcf_admin')
          ?.order('full_name');
        
        if (usersWithoutTeamData) setUsersWithoutTeam(usersWithoutTeamData);
      } catch (err) {
        console.error('Error fetching attendee options:', err);
      }
    };

    if (teamId || isPCFAdmin) {
      fetchAttendeeOptions();
    }
  }, [teamId, isPCFAdmin, allUsers]);

  // Enhanced: Build attendee options with categorization
  const attendeeOptions = isPCFAdmin ? 
    // For PCF Admin: Use allUsers with formatted labels
    allUsers?.map(user => ({
      value: user?.id,
      label: user?.displayLabel,
      category: 'All Users',
      searchText: `${user?.name} ${user?.teamNumber || user?.role}`?.toLowerCase()
    }))
    :
    // For regular team view: Use existing categorization
    [
      // Team members
      ...(teamMembers || [])?.map(member => ({
        value: member?.id,
        label: `${member?.name} (Team Member)`,
        category: 'Team Members',
        searchText: `${member?.name} team member`?.toLowerCase()
      })),
      // City Official
      ...(cityOfficial ? [{
        value: cityOfficial?.id,
        label: `${cityOfficial?.full_name} (City Official)`,
        category: 'City Official',
        searchText: `${cityOfficial?.full_name} city official`?.toLowerCase()
      }] : []),
      // Mentor
      ...(mentor ? [{
        value: mentor?.id,
        label: `${mentor?.full_name} (Mentor)`,
        category: 'Mentor',
        searchText: `${mentor?.full_name} mentor`?.toLowerCase()
      }] : []),
      // PCF Admins
      ...pcfAdmins?.map(admin => ({
        value: admin?.id,
        label: `${admin?.full_name} (PCF Admin)`,
        category: 'PCF Admins',
        searchText: `${admin?.full_name} pcf admin`?.toLowerCase()
      })),
      // Users without team
      ...usersWithoutTeam?.map(user => ({
        value: user?.id,
        label: `${user?.full_name} (${user?.role === 'city_official' ? 'City Official' : user?.role === 'student' ? 'Student' : 'Other'})`,
        category: 'Others',
        searchText: `${user?.full_name} ${user?.role}`?.toLowerCase()
      }))
    ];

  // NEW: Filter attendees based on search query
  const filteredAttendeeOptions = searchQuery?.trim()
    ? attendeeOptions?.filter(option => 
        option?.searchText?.includes(searchQuery?.toLowerCase())
      )
    : attendeeOptions;

  // Group attendees by category for better UI
  const groupedAttendees = filteredAttendeeOptions?.reduce((acc, option) => {
    const category = option?.category || 'Other';
    if (!acc?.[category]) acc[category] = [];
    acc?.[category]?.push(option);
    return acc;
  }, {});

  // Fetch meetings on component mount
  useEffect(() => {
    if (isPCFAdmin) {
      // For PCF Admin, fetch all meetings
      fetchAllMeetings();
    } else if (teamId) {
      // For team view, fetch team meetings
      fetchMeetings();
    }
  }, [teamId, isPCFAdmin]);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      // FIXED: Use getUserMeetings to get meetings where user is organizer OR attendee
      const { data, error: fetchError } = await meetingService?.getUserMeetings(user?.id, teamId);
      
      if (fetchError) throw fetchError;
      
      setMeetings(data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching meetings:', err);
      setError('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllMeetings = async () => {
    try {
      setLoading(true);
      // Fetch all meetings for PCF Admin
      const { data: meetings, error: fetchError } = await supabase
        ?.from('meetings')
        ?.select('*')
        ?.order('meeting_date', { ascending: false })
        ?.order('meeting_time', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      // Fetch attendee details for each meeting
      const meetingsWithAttendees = await Promise.all(
        (meetings || [])?.map(async (meeting) => {
          if (meeting?.attendee_ids && meeting?.attendee_ids?.length > 0) {
            const { data: attendees } = await supabase
              ?.from('user_profiles')
              ?.select('id, full_name, email, role')
              ?.in('id', meeting?.attendee_ids);

            return { ...meeting, attendees: attendees || [] };
          }
          return { ...meeting, attendees: [] };
        })
      );
      
      setMeetings(meetingsWithAttendees || []);
      setError('');
    } catch (err) {
      console.error('Error fetching all meetings:', err);
      setError('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleMeeting = async () => {
    if (!newMeeting?.title?.trim() || !newMeeting?.date || !newMeeting?.time) {
      setError('Please fill in all required fields');
      return;
    }

    if (newMeeting?.attendees?.length === 0) {
      setError('Please select at least one attendee');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // CRITICAL FIX: Wrap the entire operation in try-catch with proper error handling
      const meetingPayload = {
        teamId: isPCFAdmin ? null : teamId,
        organizerId: user?.id,
        title: newMeeting?.title,
        description: newMeeting?.description,
        meetingType: newMeeting?.type,
        date: newMeeting?.date,
        time: newMeeting?.time,
        duration: parseInt(newMeeting?.duration),
        locationType: newMeeting?.location,
        locationDetails: newMeeting?.locationDetails,
        googleMeetLink: newMeeting?.googleMeetLink,
        attendeeIds: newMeeting?.attendees
      };

      // CRITICAL: Use proper async/await without blocking UI
      const result = await meetingService?.createMeeting(meetingPayload);

      if (result?.error) {
        throw new Error(result?.error?.message || 'Failed to create meeting');
      }

      // Success - update UI
      setNewMeeting({
        title: '',
        description: '',
        date: '',
        time: '',
        duration: '60',
        type: 'team',
        attendees: [],
        location: 'online',
        locationDetails: '',
        googleMeetLink: ''
      });
      setShowScheduleForm(false);
      setSearchQuery('');

      // Refresh meetings list
      if (isPCFAdmin) {
        await fetchAllMeetings();
      } else {
        await fetchMeetings();
      }

    } catch (err) {
      console.error('Error scheduling meeting:', err);
      setError(err?.message || 'Failed to schedule meeting. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to cancel this meeting? All attendees will be notified.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const result = await meetingService?.cancelMeeting(meetingId, user?.id);

      if (result?.error) {
        throw new Error(result?.error?.message || 'Failed to cancel meeting');
      }

      // Refresh meetings list
      if (isPCFAdmin) {
        await fetchAllMeetings();
      } else {
        await fetchMeetings();
      }

      setExpandedMeeting(null);
    } catch (err) {
      console.error('Error cancelling meeting:', err);
      setError(err?.message || 'Failed to cancel meeting. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date, time) => {
    const dateTime = new Date(`${date}T${time}`);
    return dateTime?.toLocaleString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMeetingIcon = (type) => {
    switch (type) {
      case 'team': return 'Users';
      case 'city-official': return 'Building2';
      case 'mentor': return 'GraduationCap';
      case 'presentation': return 'Presentation';
      default: return 'Calendar';
    }
  };

  const getMeetingColor = (type) => {
    switch (type) {
      case 'team': return 'text-blue-600 bg-blue-50';
      case 'city-official': return 'text-green-600 bg-green-50';
      case 'mentor': return 'text-purple-600 bg-purple-50';
      case 'presentation': return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'text-primary bg-primary/10';
      case 'in-progress': return 'text-success bg-success/10';
      case 'completed': return 'text-muted-foreground bg-muted';
      case 'cancelled': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const isUpcoming = (date, time) => {
    const meetingDateTime = new Date(`${date}T${time}`);
    return meetingDateTime > new Date();
  };

  const upcomingMeetings = meetings?.filter(meeting => 
    isUpcoming(meeting?.meeting_date, meeting?.meeting_time) && meeting?.status === 'scheduled'
  )?.sort((a, b) => new Date(`${a.meeting_date}T${a.meeting_time}`) - new Date(`${b.meeting_date}T${b.meeting_time}`));

  const pastMeetings = meetings?.filter(meeting => 
    !isUpcoming(meeting?.meeting_date, meeting?.meeting_time) || meeting?.status === 'completed'
  )?.sort((a, b) => new Date(`${b.meeting_date}T${b.meeting_time}`) - new Date(`${a.meeting_date}T${a.meeting_time}`));

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Meeting Scheduler</h2>
        <Button 
          variant="default" 
          size="sm" 
          iconName="Plus" 
          iconPosition="left"
          onClick={() => setShowScheduleForm(true)}
          disabled={loading}
        >
          Schedule Meeting
        </Button>
      </div>
      {error && (
        <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-error" />
            <span className="text-sm text-error">{error}</span>
          </div>
        </div>
      )}
      {/* Schedule Meeting Form */}
      {showScheduleForm && (
        <div className="mb-6 p-4 border border-border rounded-lg bg-muted/50">
          <h3 className="text-lg font-medium text-foreground mb-4">Schedule New Meeting</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Meeting Title"
              type="text"
              placeholder="Enter meeting title"
              value={newMeeting?.title}
              onChange={(e) => setNewMeeting(prev => ({ ...prev, title: e?.target?.value }))}
              required
            />
            <Select
              label="Meeting Type"
              options={meetingTypes}
              value={newMeeting?.type}
              onChange={(value) => setNewMeeting(prev => ({ ...prev, type: value }))}
            />
            <Input
              label="Date"
              type="date"
              value={newMeeting?.date}
              onChange={(e) => setNewMeeting(prev => ({ ...prev, date: e?.target?.value }))}
              required
            />
            <Input
              label="Time"
              type="time"
              value={newMeeting?.time}
              onChange={(e) => setNewMeeting(prev => ({ ...prev, time: e?.target?.value }))}
              required
            />
            <Select
              label="Duration"
              options={durationOptions}
              value={newMeeting?.duration}
              onChange={(value) => setNewMeeting(prev => ({ ...prev, duration: value }))}
            />
            <Select
              label="Location"
              options={locationOptions}
              value={newMeeting?.location}
              onChange={(value) => setNewMeeting(prev => ({ ...prev, location: value }))}
            />
          </div>

          {/* Google Meet Link - Show only for online meetings */}
          {newMeeting?.location === 'online' && (
            <div className="mt-4">
              <Input
                label="Google Meet Link"
                type="url"
                placeholder="Paste your Google Meet link here"
                value={newMeeting?.googleMeetLink}
                onChange={(e) => setNewMeeting(prev => ({ ...prev, googleMeetLink: e?.target?.value }))}
              />
            </div>
          )}

          {/* Location Details - Show for non-online meetings */}
          {newMeeting?.location !== 'online' && (
            <div className="mt-4">
              <Input
                label="Location Details"
                type="text"
                placeholder="Enter specific location details"
                value={newMeeting?.locationDetails}
                onChange={(e) => setNewMeeting(prev => ({ ...prev, locationDetails: e?.target?.value }))}
              />
            </div>
          )}

          <div className="mt-4">
            <Input
              label="Description"
              type="text"
              placeholder="Enter meeting description"
              value={newMeeting?.description}
              onChange={(e) => setNewMeeting(prev => ({ ...prev, description: e?.target?.value }))}
            />
          </div>

          {/* ENHANCED: Attendees Multi-Select with Search */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Select Attendees <span className="text-error">*</span>
            </label>
            
            {/* NEW: Search Bar */}
            <div className="mb-3">
              <div className="relative">
                <Icon 
                  name="Search" 
                  size={18} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                />
                <input
                  type="text"
                  placeholder="Search by name, team number, or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="X" size={16} />
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-xs text-muted-foreground mt-1">
                  Found {filteredAttendeeOptions?.length} result(s)
                </p>
              )}
            </div>

            <div className="border border-border rounded-md max-h-64 overflow-y-auto p-3 space-y-2">
              {Object?.entries(groupedAttendees || {})?.map(([category, options]) => (
                <div key={category} className="mb-3">
                  <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                    {category}
                  </div>
                  {options?.map((option) => (
                    <label
                      key={option?.value}
                      className="flex items-center space-x-2 p-2 hover:bg-muted rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={newMeeting?.attendees?.includes(option?.value)}
                        onChange={(e) => {
                          if (e?.target?.checked) {
                            setNewMeeting({
                              ...newMeeting,
                              attendees: [...newMeeting?.attendees, option?.value]
                            });
                          } else {
                            setNewMeeting({
                              ...newMeeting,
                              attendees: newMeeting?.attendees?.filter(id => id !== option?.value)
                            });
                          }
                        }}
                        className="rounded border-border text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-foreground">{option?.label}</span>
                    </label>
                  ))}
                </div>
              ))}
              {filteredAttendeeOptions?.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No attendees found
                </p>
              )}
            </div>

            {/* Selected Count */}
            {newMeeting?.attendees?.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                {newMeeting?.attendees?.length} attendee(s) selected
              </p>
            )}
          </div>

          <div className="flex items-center space-x-2 mt-4">
            <Button 
              variant="default" 
              onClick={handleScheduleMeeting}
              loading={loading}
              disabled={loading}
            >
              Schedule Meeting
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowScheduleForm(false);
                setError('');
                setSearchQuery(''); // Clear search when closing
              }}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {/* Upcoming Meetings */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Upcoming Meetings</h3>
        {loading && upcomingMeetings?.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <Icon name="Loader2" size={24} className="animate-spin mx-auto mb-2" />
            <p>Loading meetings...</p>
          </div>
        ) : upcomingMeetings?.length === 0 ? (
          <div className="p-4 border border-dashed border-border rounded-lg text-center">
            <Icon name="Calendar" size={48} className="mx-auto mb-2 text-muted-foreground" />
            <p className="text-muted-foreground">No upcoming meetings scheduled</p>
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingMeetings?.map((meeting) => (
              <div key={meeting?.id} className="border border-border rounded-lg">
                {/* Meeting Card Header - Always Visible */}
                <div 
                  className="p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => setExpandedMeeting(expandedMeeting === meeting?.id ? null : meeting?.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className={`p-2 rounded-lg ${
                        meeting?.meeting_type === 'team' ? 'text-blue-600 bg-blue-50' :
                        meeting?.meeting_type === 'city-official' ? 'text-green-600 bg-green-50' :
                        meeting?.meeting_type === 'mentor'? 'text-purple-600 bg-purple-50' : 'text-orange-600 bg-orange-50'
                      }`}>
                        <Icon name={
                          meeting?.meeting_type === 'team' ? 'Users' :
                          meeting?.meeting_type === 'city-official' ? 'Building2' :
                          meeting?.meeting_type === 'mentor'? 'GraduationCap' : 'Presentation'
                        } size={20} />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-md font-medium text-foreground">{meeting?.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {new Date(`${meeting?.meeting_date}T${meeting?.meeting_time}`)?.toLocaleString([], {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <Icon 
                      name={expandedMeeting === meeting?.id ? "ChevronUp" : "ChevronDown"} 
                      size={20} 
                      className="text-muted-foreground"
                    />
                  </div>
                </div>

                {/* Expanded Meeting Details */}
                {expandedMeeting === meeting?.id && (
                  <div className="px-4 pb-4 pt-3 border-t border-border space-y-3">
                    {meeting?.description && (
                      <div>
                        <h5 className="text-sm font-medium text-foreground mb-1">Description</h5>
                        <p className="text-sm text-muted-foreground">{meeting?.description}</p>
                      </div>
                    )}

                    {/* Attendee List */}
                    {meeting?.attendees && meeting?.attendees?.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium text-foreground mb-2 flex items-center space-x-1">
                          <Icon name="Users" size={14} className="text-muted-foreground" />
                          <span>Attendees ({meeting?.attendees?.length})</span>
                        </h5>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {meeting?.attendees?.map((attendee) => (
                            <div
                              key={attendee?.id}
                              className="px-3 py-2 bg-muted/50 text-foreground rounded-md border border-border"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-foreground">
                                    {attendee?.full_name || attendee?.name}
                                  </p>
                                  {attendee?.role && (
                                    <p className="text-xs text-muted-foreground mt-0.5 capitalize">
                                      {attendee?.role === 'pcf_admin' ? 'PCF Admin' :
                                       attendee?.role === 'city_official' ? 'City Official' :
                                       attendee?.role === 'business' ? 'Business Partner' :
                                       attendee?.role}
                                    </p>
                                  )}
                                  {attendee?.email && (
                                    <p className="text-xs text-muted-foreground mt-1 flex items-center space-x-1">
                                      <Icon name="Mail" size={12} className="text-muted-foreground" />
                                      <span>{attendee?.email}</span>
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {meeting?.attendees?.length === 0 && (
                      <div className="text-sm text-muted-foreground py-2">
                        No attendees added
                      </div>
                    )}

                    {/* Meeting Details Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center space-x-2">
                        <Icon name="Clock" size={14} className="text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{meeting?.duration_minutes} min</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="MapPin" size={14} className="text-muted-foreground" />
                        <span className="text-sm text-muted-foreground capitalize">{meeting?.location_type}</span>
                      </div>
                    </div>

                    {/* Cancel Meeting Button - Only for organizer */}
                    {meeting?.organizer_id === user?.id && meeting?.status !== 'cancelled' && (
                      <div className="pt-2 border-t border-border">
                        <Button
                          variant="outline"
                          onClick={(e) => {
                            e?.stopPropagation();
                            handleCancelMeeting(meeting?.id);
                          }}
                          iconName="XCircle"
                          iconPosition="left"
                          className="w-full text-error border-error hover:bg-error/10"
                        >
                          Cancel Meeting
                        </Button>
                      </div>
                    )}

                    {/* Cancelled Status Badge */}
                    {meeting?.status === 'cancelled' && (
                      <div className="pt-2 border-t border-border">
                        <div className="px-3 py-2 bg-error/10 text-error rounded-lg text-center text-sm font-medium">
                          This meeting has been cancelled
                        </div>
                      </div>
                    )}

                    {/* Meeting Link */}
                    {meeting?.google_meet_link && (
                      <div className="pt-2 border-t border-border">
                        <a 
                          href={meeting?.google_meet_link?.startsWith('http') ? meeting?.google_meet_link : `https://${meeting?.google_meet_link}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                          onClick={(e) => {
                            e?.stopPropagation();
                          }}
                        >
                          <Icon name="Video" size={16} />
                          <span>Join Meeting</span>
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Past Meetings */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Past Meetings</h3>
        {pastMeetings?.length === 0 ? (
          <div className="p-4 border border-dashed border-border rounded-lg text-center">
            <p className="text-muted-foreground">No past meetings</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {pastMeetings?.slice(0, 5)?.map((meeting) => (
              <div key={meeting?.id} className="p-3 border border-border rounded-lg opacity-75">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-1.5 rounded ${
                      meeting?.meeting_type === 'team' ? 'text-blue-600 bg-blue-50' :
                      meeting?.meeting_type === 'city-official' ? 'text-green-600 bg-green-50' :
                      meeting?.meeting_type === 'mentor'? 'text-purple-600 bg-purple-50' : 'text-orange-600 bg-orange-50'
                    }`}>
                      <Icon name={
                        meeting?.meeting_type === 'team' ? 'Users' :
                        meeting?.meeting_type === 'city-official' ? 'Building2' :
                        meeting?.meeting_type === 'mentor'? 'GraduationCap' : 'Presentation'
                      } size={16} />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground">{meeting?.title}</h4>
                      <p className="text-xs text-muted-foreground">
                        {new Date(`${meeting?.meeting_date}T${meeting?.meeting_time}`)?.toLocaleString([], {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    meeting?.status === 'completed' ? 'text-muted-foreground bg-muted' :
                    meeting?.status === 'cancelled'? 'text-error bg-error/10' : 'text-primary bg-primary/10'
                  }`}>
                    {meeting?.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingScheduler;